import React, { useMemo } from "react";
import { Box } from "@mui/material";
import { Theme } from "../../../../providers/ThemeProvider";
import useTheme from "../../../../common/hooks/useTheme";
import PageLayout from "../layout/PageLayout";
import PageTitleSection from "../common/PageTitleSection";
import { SchedulingPage } from "../../types/newReportTypes";
import {
  AVAILABLE_CONTENT_HEIGHT,
  SCHEDULING_HEIGHTS,
} from "../../hooks/usePagination";
import { useSchedulingData, GroupedServices } from "../../hooks/useSchedulingData";
import SchedulingTable from "../common/SchedulingTable";

type Props = {
  page: SchedulingPage;
  pageIndex: number;
};

type PageContent = {
  weeklyGroups: GroupedServices[];
  quarterlyGroups: GroupedServices[];
};

const ServiceSchedulingPage = ({ page, pageIndex }: Props) => {
  const theme: Theme = useTheme();

  // Use shared height constants
  const {
    TITLE_HEIGHT,
    SECTION_HEADER_HEIGHT,
    TABLE_HEADER_HEIGHT,
    SERVICE_ROW_HEIGHT,
    CATEGORY_OVERHEAD,
    PADDING_GAPS,
  } = SCHEDULING_HEIGHTS;

  // Use shared hook to group services
  const { weeklyGroups: allWeeklyGroups, quarterlyGroups: allQuarterlyGroups } =
    useSchedulingData({
      schedule: page.schedule,
      serviceTypeLabels: page.labels.serviceTypes,
    });

  // Calculate pages based on fixed heights - no rendering needed!
  const pages = useMemo(() => {
    const result: PageContent[] = [];
    let currentPage: PageContent = { weeklyGroups: [], quarterlyGroups: [] };
    let currentHeight = TITLE_HEIGHT + PADDING_GAPS;

    // Helper to split services within a group
    const splitGroupServices = (
      group: GroupedServices,
      availableHeight: number,
      includeSectionHeader: boolean
    ): {
      fitted: GroupedServices | null;
      remaining: GroupedServices | null;
    } => {
      const sectionOverhead = includeSectionHeader
        ? SECTION_HEADER_HEIGHT + TABLE_HEADER_HEIGHT
        : 0;

      const heightForServices =
        availableHeight - sectionOverhead - CATEGORY_OVERHEAD;
      const maxServices = Math.floor(heightForServices / SERVICE_ROW_HEIGHT);

      if (maxServices <= 0) {
        return { fitted: null, remaining: group };
      }

      if (maxServices >= group.services.length) {
        return { fitted: group, remaining: null };
      }

      // Split the services array
      return {
        fitted: {
          ...group,
          services: group.services.slice(0, maxServices),
        },
        remaining: {
          ...group,
          services: group.services.slice(maxServices),
        },
      };
    };

    // Process a section (weekly or quarterly)
    const addSection = (
      groups: GroupedServices[],
      isWeekly: boolean
    ): GroupedServices[] => {
      if (groups.length === 0) return [];

      const remaining: GroupedServices[] = [];
      let sectionStarted = false;

      for (let i = 0; i < groups.length; i++) {
        const group = groups[i];
        const sectionOverhead = !sectionStarted
          ? SECTION_HEADER_HEIGHT + TABLE_HEADER_HEIGHT
          : 0;

        const availableSpace = AVAILABLE_CONTENT_HEIGHT - currentHeight;
        const { fitted, remaining: leftover } = splitGroupServices(
          group,
          availableSpace,
          !sectionStarted
        );

        if (fitted) {
          const fittedHeight =
            sectionOverhead +
            CATEGORY_OVERHEAD +
            fitted.services.length * SERVICE_ROW_HEIGHT;

          currentHeight += fittedHeight;
          sectionStarted = true;

          if (isWeekly) {
            currentPage.weeklyGroups.push(fitted);
          } else {
            currentPage.quarterlyGroups.push(fitted);
          }
        }

        if (leftover) {
          // Add leftover from current group
          remaining.push(leftover);
          // Add all unprocessed groups
          remaining.push(...groups.slice(i + 1));
          break;
        }
      }

      return remaining;
    };

    // Process weekly groups
    let remainingWeekly = addSection(allWeeklyGroups, true);
    // Process quarterly groups
    let remainingQuarterly = addSection(allQuarterlyGroups, false);

    // Save first page if it has content
    if (
      currentPage.weeklyGroups.length > 0 ||
      currentPage.quarterlyGroups.length > 0
    ) {
      result.push(currentPage);
    }

    // Process remaining content across additional pages
    let maxIterations = 50; // Safety limit
    while (
      (remainingWeekly.length > 0 || remainingQuarterly.length > 0) &&
      maxIterations-- > 0
    ) {
      currentPage = { weeklyGroups: [], quarterlyGroups: [] };
      currentHeight = PADDING_GAPS; // Continuation pages have no title, only padding

      remainingWeekly = addSection(remainingWeekly, true);
      remainingQuarterly = addSection(remainingQuarterly, false);

      if (
        currentPage.weeklyGroups.length > 0 ||
        currentPage.quarterlyGroups.length > 0
      ) {
        result.push(currentPage);
      } else {
        // No content added - prevent infinite loop
        console.error("Pagination error: Unable to fit content");
        break;
      }
    }

    return result.length > 0
      ? result
      : [{ weeklyGroups: [], quarterlyGroups: [] }];
  }, [
    page.schedule,
    allWeeklyGroups,
    allQuarterlyGroups,
    TITLE_HEIGHT,
    SECTION_HEADER_HEIGHT,
    TABLE_HEADER_HEIGHT,
    SERVICE_ROW_HEIGHT,
    CATEGORY_OVERHEAD,
    PADDING_GAPS,
  ]);

  const renderSchedulingSection = (
    groups: GroupedServices[],
    isWeekly: boolean
  ) => {
    if (groups.length === 0) return null;

    return (
      <Box sx={styles.section}>
        {/* Section Title */}
        <Box component="p" sx={styles.sectionTitle(theme)}>
          {isWeekly
            ? page.labels.weeklySchedule
            : page.labels.quarterlySchedule}
        </Box>

        {/* Use shared SchedulingTable component */}
        <SchedulingTable
          groups={groups}
          isWeekly={isWeekly}
          labels={{
            serviceName: page.labels.serviceName,
            week: page.labels.week,
            quarter: page.labels.quarter,
          }}
        />
      </Box>
    );
  };

  return (
    <>
      {pages.map((pageContent, idx) => (
        <PageLayout
          key={`${page.id}-${idx}`}
          companyName={page.companyName}
          sectionName={page.sectionName}
          pageIndex={pageIndex + idx}
        >
          <Box sx={styles.pageContent}>
            {/* Page Title & Subtitle - only on first page */}
            {idx === 0 && (
              <PageTitleSection
                title={page.labels.title}
                subtitle={page.labels.subtitle}
              />
            )}

            {/* Weekly Schedule Section */}
            {renderSchedulingSection(pageContent.weeklyGroups, true)}

            {/* Quarterly Schedule Section */}
            {renderSchedulingSection(pageContent.quarterlyGroups, false)}
          </Box>
        </PageLayout>
      ))}
    </>
  );
};

export default ServiceSchedulingPage;

const styles = {
  pageContent: {
    display: "flex",
    flexDirection: "column" as "column",
    padding: "20px 18px",
    gap: "18px",
  },
  section: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
  },
  sectionTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "11px",
    lineHeight: "normal",
    color: theme.colorTokens.N[90], // #3B3F42
    margin: 0,
  }),
};
